export const DEFAULT_POST_AUTH_DESTINATION = '/app';
export const POST_AUTH_REDIRECT_STORAGE_KEY = 'allVoiced.postAuthRedirect';
export const POST_AUTH_REDIRECT_TTL_MS = 30 * 60 * 1000;

function containsControlCharacter(value) {
  return Array.from(value).some((character) => {
    const codePoint = character.charCodeAt(0);
    return codePoint <= 31 || codePoint === 127;
  });
}

function getSessionStorage() {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage || null;
  } catch (error) {
    return null;
  }
}

function normalizePathParts(pathname = '', search = '', hash = '') {
  const safeSearch = search && search.startsWith('?') ? search : '';
  const safeHash = hash && hash.startsWith('#') ? hash : '';
  return `${pathname}${safeSearch}${safeHash}`;
}

export function getPathFromLocation(location) {
  if (!location) return '';

  if (typeof location === 'string') {
    return location;
  }

  return normalizePathParts(
    location.pathname,
    location.search,
    location.hash
  );
}

export function isSafeInternalRedirect(destination) {
  if (typeof destination !== 'string') return false;

  const trimmedDestination = destination.trim();

  if (!trimmedDestination) return false;
  if (trimmedDestination !== destination) return false;
  if (!trimmedDestination.startsWith('/')) return false;
  if (trimmedDestination.startsWith('//')) return false;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmedDestination)) return false;
  if (containsControlCharacter(trimmedDestination)) return false;

  return (
    trimmedDestination === '/app' ||
    trimmedDestination.startsWith('/app/')
  );
}

export function getSafePostAuthDestination(
  destination,
  fallback = DEFAULT_POST_AUTH_DESTINATION
) {
  return isSafeInternalRedirect(destination)
    ? destination
    : fallback;
}

export function createPostAuthDestination(location) {
  const destination = getPathFromLocation(location);
  return isSafeInternalRedirect(destination)
    ? destination
    : null;
}

export function persistPostAuthRedirect(destination) {
  const safeDestination = getSafePostAuthDestination(destination, null);
  const storage = getSessionStorage();

  if (!safeDestination || !storage) return;

  storage.setItem(
    POST_AUTH_REDIRECT_STORAGE_KEY,
    JSON.stringify({
      destination: safeDestination,
      createdAt: Date.now(),
    })
  );
}

export function clearPostAuthRedirect() {
  const storage = getSessionStorage();
  if (!storage) return;

  storage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY);
}

export function getStoredPostAuthRedirect() {
  const storage = getSessionStorage();
  if (!storage) return null;

  const storedValue = storage.getItem(POST_AUTH_REDIRECT_STORAGE_KEY);

  if (!storedValue) return null;

  try {
    const parsedValue = JSON.parse(storedValue);
    const { destination, createdAt } = parsedValue || {};
    const isExpired =
      typeof createdAt !== 'number' ||
      Date.now() - createdAt > POST_AUTH_REDIRECT_TTL_MS;

    if (isExpired || !isSafeInternalRedirect(destination)) {
      clearPostAuthRedirect();
      return null;
    }

    return destination;
  } catch (error) {
    clearPostAuthRedirect();
    return null;
  }
}

export function resolvePostAuthRedirect(
  locationState,
  fallback = DEFAULT_POST_AUTH_DESTINATION
) {
  const stateDestination = createPostAuthDestination(locationState?.from);

  if (stateDestination) {
    return stateDestination;
  }

  return getStoredPostAuthRedirect() || fallback;
}

export function consumePostAuthRedirect(
  locationState,
  fallback = DEFAULT_POST_AUTH_DESTINATION
) {
  const destination = resolvePostAuthRedirect(locationState, fallback);
  clearPostAuthRedirect();
  return destination;
}
